import { useState, useRef, Suspense, lazy, useEffect } from "react";
import Dropzone from "react-dropzone";
import ReactToolip from 'react-tooltip';
import { UPLOAD_ICON } from "../../common/images-s3";
import { CHATBOAT_UPLOAD_ICON } from "../../common/images-svg";
import { Common } from "../../common/common";

const ImageLoader = lazy(() => import('../../common/image-loader'));

const FileUpload = (props) => {
    const [isUploaded, setIsUploaded] = useState(false);
    const [isDropzoneDisabled, setIsDropzoneDisabled] = useState(false);
    const [fileError, setFileError] = useState("");
    const [uploadedFilesArray, setUploadedFilesArray] = useState([]);
    const [unsupportedFileArray, setUnsupportedFileArray] = useState([]);

    let errorTimerRef = useRef(null);
    let isMounted = useRef(null);

    // File sizes and accepted formats
    const sizes = ['Bytes', 'KB', 'MB'];
    const reqFormat = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];

    // To handle memomy leak warning after component unmounted
    useEffect(() => {
        isMounted.current = true;
        return () => isMounted.current = false;
    }, [])

    useEffect(() => {
        props.setFIleDetails(uploadedFilesArray);
        !uploadedFilesArray.length && props.setAttachmentError('');
        setIsDropzoneDisabled(uploadedFilesArray.length >= 1);
    }, [uploadedFilesArray])
    
    useEffect(() => {
        if (Boolean(props.isRemoveFile)) {
            setUploadedFilesArray([]);
            setIsUploaded(false);
            setIsDropzoneDisabled(false);
            setFileError("");
            setUnsupportedFileArray([]);
        }
    }, [props.isRemoveFile]);

    const formatBytes = (bytes) => {
        if (!+bytes) return '0 Bytes';

        const k = 1024;
        const dm = 2;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`
    }

    // Validate file for valid file format and size
    const validateUploadedFile = (acceptedFile) => {
        const fileData = formatBytes(acceptedFile?.size).split(' ');
        const validFileCategory = sizes.some(item => fileData[1] === item);

        let validFileSize = null;
        // let invalidFile = !reqFormat.includes(acceptedFiles[0]?.type)
        let validFile = reqFormat.includes(acceptedFile?.type)

        switch (true) {
            case fileData[1] === 'MB':
                validFileSize = Number(fileData[0]) > 0 && Number(fileData[0]) <= 10.00;
                break;
            default:
                validFileSize = Number(fileData[0]) > 0;
                break;
        }

        return { validFile, validFileSize, validFileCategory }
    }

    // Returns error message based on number of unsupported and greater size files
    const getErrorMessage = (unsupported, sizeExceeded) => {
        let error = ''
        if (!sizeExceeded.length && unsupported.length) {
            error = unsupported.length === 1
                ? `file format is unsupported. Supported File Types: pdf, docx`
                : `file formats are unsupported. Supported File Types: pdf, docx`;
            return error;
        }

        if (sizeExceeded.length && !unsupported.length) {
            error = sizeExceeded.length === 1
                ? `${sizeExceeded.length} file exceeds size limit of 10 MB.`
                : `${sizeExceeded.length} files exceed size limit of 10 MB.`;
            return error;
        }

        if (sizeExceeded.length && unsupported.length) {
            const unsupportedError = unsupported.length === 1
                ? `file format is unsupported`
                : `file formats are unsupported`;
            const sizeError = sizeExceeded.length === 1
                ? `${sizeExceeded.length} file exceeds size limit of 10 MB`
                : `${sizeExceeded.length} files exceed size limit of 10 MB`;

            error = `${unsupportedError} and ${sizeError}. Supported File Types: pdf, docx`;
            return error;
        }

        return error;
    }

    const onChangeUpload = (acceptedFiles) => {
        const files = uploadedFilesArray ?? [];
        const unsupportedFiles = [];
        const sizeExceededFiles = [];

        acceptedFiles?.map(file => {
            const { validFile, validFileSize, validFileCategory } = validateUploadedFile(file);
            if (validFile && validFileSize && validFileCategory) {
                files.push(file);
                props.setAttachmentError('');
            } else {
                if (!validFileSize && validFile) {
                    sizeExceededFiles.push(file);
                } else {
                    unsupportedFiles.push(file);
                }
            }
        })

        setIsUploaded(files.length > 0);
        setUploadedFilesArray([...files]);
        setUnsupportedFileArray([...unsupportedFiles]);

        const errorMessage = getErrorMessage(unsupportedFiles, sizeExceededFiles);
        setFileError(errorMessage);

        // Clear Error Message after 10 seconds automatically
        if (errorMessage) {
            errorTimerRef && clearTimeout(errorTimerRef);
            errorTimerRef = setTimeout(() => {
                (isMounted.current !== false) && setFileError('');
            }, 10000);
        }
    }

    const handleFileRemove = (removedFile) => {
        const files = uploadedFilesArray.filter(file => file.name !== removedFile.name);
        setUploadedFilesArray(files);
        props.resetFileObject();
        Boolean(!files.length) && setFileError('');
    }

    const chatBot = props?.style === "chatBot";

    return (<>
        <div className={`${chatBot ? "chatboat-upload" : "row"}`}>
            <div className={`${chatBot ?  "" : "col-lg-7 col-md-12"}`}>
                <div className={"c-upload " + (props.hasError ? "has-error" : "")}>
                    <Dropzone
                        multiple={false}
                        maxFiles={1}
                        disabled={isDropzoneDisabled}
                        accept="application/pdf,  'application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                        onDrop={acceptedFiles => onChangeUpload(acceptedFiles)}
                    >
                        {({ getRootProps, getInputProps }) => (
                            <section>
                                <div {...getRootProps()}>
                                    <input id="uploadDoc" type="file" name="user[image]" className="hide" {...getInputProps()}
                                        accept={`.pdf, .docx`}></input>
                                    <span className="text-xl hover:text-gray-800"> {chatBot ? CHATBOAT_UPLOAD_ICON : '📎'}</span>
                                </div>
                            </section>
                        )}
                    </Dropzone>

                    {
                        Boolean(!unsupportedFileArray.length && fileError) &&
                        <div className="invalid-feedback">{fileError}</div>
                    }
                     {
                        Boolean(unsupportedFileArray.length && fileError)
                            ? <div className="invalid-feedback">{fileError}</div>
                            : null
                    }
                </div>
            </div>
            {
                Boolean(uploadedFilesArray.length) && (
                    <div className="attachment-col">
                    <div className="common-attachment-ui">
                        {uploadedFilesArray.map((file, index) => (
                        <UploadedFile key={index} fileObject={file} onRemoveFile={handleFileRemove} isShowFile={props.isShowfile}/>
                        ))}
                    </div>
                    </div>
                )
                }
        </div>

    </>)
}

export default FileUpload;

const UploadedFile = ({ fileObject, onRemoveFile, isShowFile }) => {
    return (
       <>
            {isShowFile ? <div className='c-attachment common-attachment-list'>
                <div className='c-file'>
                    <span className="cursor-pointer" data-tip={fileObject?.name} data-for='remark-tooltip' data-arrow-color='#47545B'>{ Common.isMobile() && fileObject?.name?.length > 33 ? `${fileObject.name.slice(0, 33)}...` : fileObject?.name} </span>
                    <ReactToolip id='remark-tooltip' effect={'solid'} html={true} className='table-tooltip'></ReactToolip>
                </div>
                <button
                    className="btn-attachment "
                    type="button" // IMPORTANT TO AVOID FORM SUBMIT ON FILE REMOVE
                    onClick={() => onRemoveFile(fileObject)}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="11.199" height="11.25" viewBox="0 0 11.199 11.25">
                        <path id="close" d="M6.508,5.636l4.283-4.283A.713.713,0,0,0,9.783.345L5.5,4.628,1.217.345A.713.713,0,0,0,.209,1.352L4.492,5.636.209,9.919a.713.713,0,1,0,1.008,1.008L5.5,6.643l4.283,4.283a.713.713,0,0,0,1.008-1.008Zm0,0" transform="translate(0.1 0.014)" fill="#9ea8b1" stroke="#9ea8b1" strokeWidth="0.2" />
                    </svg>
                </button>
            </div> : <div className='c-attachment common-attachment-list fleckring-effect'>
                <div className='c-file'>
                    <span className="cursor-pointer" data-tip={''} data-for='remark-tooltip' data-arrow-color='#47545B'>{''} </span>
                    {/* <ReactToolip id='remark-tooltip' effect={'solid'} html={true} className='table-tooltip'></ReactToolip> */}
                </div>
                <button
                    className="btn-attachment "
                    // type="button" // IMPORTANT TO AVOID FORM SUBMIT ON FILE REMOVE
                    // onClick={() => onRemoveFile(fileObject)}
                >
                    {/* <svg xmlns="http://www.w3.org/2000/svg" width="11.199" height="11.25" viewBox="0 0 11.199 11.25">
                        <path id="close" d="M6.508,5.636l4.283-4.283A.713.713,0,0,0,9.783.345L5.5,4.628,1.217.345A.713.713,0,0,0,.209,1.352L4.492,5.636.209,9.919a.713.713,0,1,0,1.008,1.008L5.5,6.643l4.283,4.283a.713.713,0,0,0,1.008-1.008Zm0,0" transform="translate(0.1 0.014)" fill="#9ea8b1" stroke="#9ea8b1" strokeWidth="0.2" />
                    </svg> */}
                </button>
            </div> }
       </>
    )
}